
import { supabase } from "@/integrations/supabase/client";
import { Lead } from "@/types";
import { toast } from "@/hooks/use-toast";
import { checkDuplicateLead } from "./leadValidation";
import { MOCK_DEALER_ID } from "./constants";

export const saveLeadToSupabase = async (lead: Lead): Promise<{ success: boolean, error?: string, isDuplicate?: boolean, existingLead?: Lead }> => {
  try {
    // First check if this lead already exists with the same email or phone
    const existingLead = await checkDuplicateLead(lead.email, lead.phone);
    
    if (existingLead) {
      console.log("Duplicate lead found:", existingLead);
      return { 
        success: false, 
        isDuplicate: true,
        existingLead,
        error: `A lead with this ${lead.email === existingLead.email ? 'email' : 'phone number'} already exists` 
      };
    }
    
    console.log("Saving lead to Supabase:", lead);
    const { data, error } = await supabase
      .from('leads')
      .insert(lead)
      .select();
    
    if (error) {
      console.error("Supabase error:", error);
      throw new Error(`Supabase error: ${error.message} (code: ${error.code})`);
    }
    console.log("Lead saved successfully:", data);
    return { success: true };
  } catch (error) {
    console.error("Error saving lead to Supabase:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Unknown error occurred" 
    };
  }
};

export const saveMultipleLeadsToSupabase = async (leads: Lead[]): Promise<{ 
  success: boolean, 
  error?: string, 
  count: number,
  duplicateCount: number,
  duplicates?: Array<{lead: Lead, existingLead: Lead}> 
}> => {
  try {
    console.log(`Processing ${leads.length} leads for duplicate check...`);
    
    // Check each lead for duplicates
    const duplicateResults = await Promise.all(
      leads.map(async (lead) => {
        const existingLead = await checkDuplicateLead(lead.email, lead.phone);
        return { lead, isDuplicate: !!existingLead, existingLead };
      })
    );
    
    // Filter out duplicates
    const uniqueLeads = duplicateResults
      .filter(result => !result.isDuplicate)
      .map(result => result.lead);
      
    const duplicates = duplicateResults
      .filter(result => result.isDuplicate)
      .map(result => ({ 
        lead: result.lead, 
        existingLead: result.existingLead as Lead 
      }));
    
    console.log(`Found ${duplicates.length} duplicates, inserting ${uniqueLeads.length} unique leads`);
    
    if (uniqueLeads.length === 0) {
      return { 
        success: false, 
        count: 0,
        duplicateCount: duplicates.length,
        duplicates,
        error: "All leads were duplicates of existing records" 
      };
    }
    
    // Insert only the unique leads
    console.log(`Saving ${uniqueLeads.length} unique leads to Supabase. First lead:`, uniqueLeads[0]);
    console.log("Dealer ID being used:", MOCK_DEALER_ID);
    const { error } = await supabase
      .from('leads')
      .insert(uniqueLeads);
    
    if (error) {
      console.error("Supabase batch insert error:", error);
      throw new Error(`Supabase error: ${error.message} (code: ${error.code})`);
    }
    console.log("Leads saved successfully");
    
    return { 
      success: true, 
      count: uniqueLeads.length,
      duplicateCount: duplicates.length,
      duplicates: duplicates.length > 0 ? duplicates : undefined
    };
  } catch (error) {
    console.error("Error saving leads to Supabase:", error);
    return { 
      success: false, 
      count: 0,
      duplicateCount: 0,
      error: error instanceof Error ? error.message : "Unknown error occurred" 
    };
  }
};
